// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FormFieldIcon } from "@insite/mobius/FormField";
import Select, { SelectComponentProps, SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ChevronDownOffset } from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles, iconScalableStyles, visuallyHiddenStyles } from "@toolkit/Themes/common/mixins";
import React, { Children, FC, useEffect, useMemo, useRef, useState } from "react";
import { css } from "styled-components";

interface Props extends SelectComponentProps, ExpandStyles<SelectInlineStyles> {}

export interface SelectInlineStyles {
    wrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const selectInlineStyles: SelectInlineStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    select: {
        labelProps: {
            css: css`
                ${x => x.theme.formField.defaultProps?.labelProps?.css}
                padding: 0;
                margin-right: 0.4em;
                font-size: inherit;
            `,
        },
        cssOverrides: {
            formField: css`
                width: auto;
            `,
            formInputWrapper: css`
                display: flex;

                ${FormFieldIcon} {
                    ${iconScalableStyles("1em", ChevronDownOffset)}
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    color: inherit;
                }
            `,
            inputSelect: css`
                ${focusStyles()}
                display: block;
                width: auto;
                height: auto;
                background-color: transparent;
                color: inherit;
                font-size: inherit;

                &:not(#important) {
                    border: 0; /* Prevent border to appear on focus */
                    border-radius: 0; /* Prevent radius to appear on focus */
                    padding: 0 1.2em 0 0; /* Override default padding assigned to fields */
                }
            `,
        },
    },
};

const SelectInline: FC<Props> = ({ extendedStyles, overridedStyles, children, ...selectProps }) => {
    const styles = useExpandProps(selectInlineStyles, extendedStyles, overridedStyles);
    const selectRef = useRef<HTMLSelectElement>(null);
    const fakeSelectRef = useRef<HTMLSelectElement>(null);
    const [isReady, setIsReady] = useState(false);

    const selectedChildren = Children.toArray(children).find((x: any) => x.props?.value === selectProps.value);

    useEffect(() => {
        setIsReady(true);
    }, []);

    useEffect(() => {
        const select = selectRef.current;

        if (select) {
            const fakeSelect = fakeSelectRef.current;

            if (fakeSelect && !selectedChildren) {
                const selectedOption = [...select.options].find(x => x.value === selectProps.value);
                fakeSelect.options[0].text = selectedOption?.text || "";
            }

            select.style.width = fakeSelect?.offsetWidth ? pxToRem(fakeSelect.offsetWidth + 2) : "";
        }
    }, [selectProps.value]);

    const hiddenSelectStyles = useMemo(() => {
        return mergeProps<SelectPresentationProps>(styles.select, {
            cssOverrides: {
                formField: visuallyHiddenStyles(),
                formInputWrapper: css`
                    position: absolute;
                    width: auto;
                `,
                inputSelect: css`
                    width: auto;
                `,
            },
        });
    }, [styles.select]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Select
                {...(isReady || !selectedChildren ? styles.select : hiddenSelectStyles)}
                {...selectProps}
                uid={selectProps.uid}
                ref={selectRef}
            >
                {children}
            </Select>
            <Select
                {...(isReady || !selectedChildren ? hiddenSelectStyles : styles.select)}
                {...selectProps}
                uid={`${selectProps.uid}_fake`}
                value={selectProps.value}
                ref={fakeSelectRef}
                aria-hidden="true"
                tabIndex={-1}
            >
                {selectedChildren || <option></option>}
            </Select>
        </StyledWrapper>
    );
};

export default SelectInline;
